import { Injectable } from '@angular/core';
import { Question, Questionnaire, QuestionResponse, Section } from '../interfaces/questions.interface';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { UserPermission } from '../../auth/enums/user-permission.enum';
import { CommonQuestionnaireService } from './common-questionnaire.service';
import * as uuid from 'uuid';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { HttpClient } from '@angular/common/http';
import { CifcService } from './cifc.service';
import { QuestionTypesEnum } from '../enums/question-types.enum';
import { ToastService } from '../../../../common-modules/services/toast.service';
import * as _ from 'lodash';
import { QuestionnaireStatusEnum } from '../enums/cifc-questionnaire-status.enum';
import { MdrDashboardsService } from '../../mdr-dashboards/services/mdr-dashboards.service';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { IdentityService } from '../../auth/services/identity.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerQuestionnaireService extends AbstractApiV2Service<Questionnaire> {
    public displayVersion = 'full';
    public showCheckSubmit: boolean = false;
    public processedFullSections: Question[] = [];
    public processedCondensedSections: Question[] = [];
    public questionnaireResumed = false;
    public sections: Section[] = [];
    public initialSections: Section[] = [];
    public questionSubject = new BehaviorSubject<Question | null>(null);
    public displayVersionSubject = new BehaviorSubject<string>('full');
    public markQuestionAsCompleteSubject = new Subject<Question>();
    public questionnaireLoadedSubject = new BehaviorSubject<boolean>(false);
    public hasSubmittedQuestionnaire: boolean;
    public companyId: string;
    public isLoading = false;
    public sectionsLoading = false;
    public isLoadingSubmit = false;
    public questionsFilteredSubscription: Subscription;
    public getQuestionnaireSubscription: Subscription;
    public answerSavedSubject = new Subject();
    protected resourceName = 'customer-questionnaire/questionnaire';
    private question: Question;
    private modifiedQuestions: QuestionResponse[] = [];
    private questionnaire: Questionnaire = {
        id: uuid.v4(),
        questionnaire: [],
        has_submitted_questionnaire: false,
        cifc_status: QuestionnaireStatusEnum.NoAnswers,
        has_answered_required: false,
        required_questions_no: 0,
        publish_in_progress: false,
        version: 0,
    };

    constructor(
        private readonly authService: AuthService,
        private readonly identityService: IdentityService,
        private readonly commonQuestionnaireService: CommonQuestionnaireService,
        private readonly cifcService: CifcService,
        private readonly toastService: ToastService,
        private readonly mdrService: MdrDashboardsService,
        http: HttpClient,
        backendErrorTranslator: BackendErrorTranslatorService,
    ) {
        super(http, backendErrorTranslator);

        this.commonQuestionnaireService.reviewMode =
            this.authService.jwtUser?.PermissionExist([UserPermission.QuestionnaireAnswerReview]) ?? false;

        if (this.commonQuestionnaireService.reviewMode) {
            this.questionnaireResumed = true;
        }

        const company = this.identityService.company;

        this.commonQuestionnaireService.questionnnaireIdLoadedObs().subscribe((questionnnaireIdLoaded) => {
            if (
                questionnnaireIdLoaded &&
                this.identityService.companyLoaded &&
                this.commonQuestionnaireService.questionnaireId &&
                !this.commonQuestionnaireService.createMode &&
                !this.commonQuestionnaireService.reviewMode &&
                this.authService.hasAtLeastOneRole([UserPermission.QuestionnaireQuestionsRead]) &&
                this.mdrService.isPremiumEnterprise()
            ) {
                this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, company.id);
                this.companyId = company.id;
            }
        });
    }

    public get showBanner(): boolean {
        return (
            this.mdrService.isPremiumEnterprise(this.identityService.company) &&
            this.identityService.isModuleAvailable('customer-questionnaire') &&
            !(localStorage.getItem('isQuestionnaireResumed') === 'true') &&
            this.questionnaire.cifc_status !== QuestionnaireStatusEnum.Completed &&
            !this.authService.jwtUser?.PermissionExist([UserPermission.QuestionnaireAnswerReview])
        );
    }

    public loadQuestionnaireById(questionnaireId: string, cid?: string, isAfterSubmit?: boolean) {
        let resourceId = questionnaireId + (cid ? `/${cid}` : '');
        this.sectionsLoading = true;
        this.isLoading = true;
        this.getQuestionnaireSubscription = this.getSingle(resourceId).subscribe(
            async (questionnaire: Questionnaire) => {
                this.sections = _.cloneDeep(questionnaire.questionnaire);
                this.initialSections = _.cloneDeep(this.sections);
                this.questionnaire.version = questionnaire.version;
                this.sections.forEach((section) => {
                    section.questions.map((question) => (question.answerChanged = false));
                });

                this.commonQuestionnaireService.reviewCompanyName = questionnaire.company_name || 'Loading company name';

                if (cid) {
                    this.commonQuestionnaireService.reviewCompanyId = cid;
                }

                this.questionnaireResumed = Boolean(localStorage.getItem('isQuestionnaireResumed')) || false;

                this.buildQuestionnaire(questionnaire);
                this.flattenSections();
                this.commonQuestionnaireService.filterQuestionsByConditionalLogicOnInit();
                this.hasSubmittedQuestionnaire = Boolean(localStorage.getItem('hasSubmittedQuestionnaire')) || false;

                this.questionsFilteredSubscription = this.commonQuestionnaireService
                    .questionsFilteredObs()
                    .subscribe(async (loaded: boolean) => {
                        if (loaded) {
                            this.setQuestion(this.processedFullSections[0]);
                            if (isAfterSubmit) {
                                this.questionnaireResumed = false;
                            }
                            this.questionsFilteredSubscription?.unsubscribe();
                        }
                    });
                this.questionnaireLoadedSubject.next(true);

                this.isLoading = false;
                this.sectionsLoading = false;
            },
            (err) => {
                this.isLoading = false;
                this.sectionsLoading = true;
                this.toastService.showError('Could not retrieve questions.', {
                    duration: 2000,
                });
            },
        );
    }

    public getQuestionnaireId() {
        if (this.mdrService.isPremiumEnterprise(this.identityService.company)) {
            this.loadAllQuestionnaires().subscribe(
                (questionnaires: Questionnaire[]) => {
                    if (questionnaires) {
                        this.commonQuestionnaireService.questionnaireId = questionnaires[0].id;
                        this.commonQuestionnaireService.isPublishInProgress = questionnaires[0].publish_in_progress;
                        this.commonQuestionnaireService.questionnaireIdLoadedSubject.next(true);
                    }
                },
                (err) => {
                    this.toastService.showError('Could not retrieve questionnaire.', { duration: 2000 });
                },
            );
        }
    }

    public loadAllQuestionnaires() {
        return this.getAll();
    }

    public showResumeBanner() {
        return (
            (this.questionnaire.cifc_status === QuestionnaireStatusEnum.InProgress ||
                this.questionnaire.cifc_status === QuestionnaireStatusEnum.Updated) &&
            this.questionnaire.has_answered_required &&
            this.questionnaire.required_questions_no !== 0
        );
    }

    public showStartBanner() {
        return (
            this.questionnaire.cifc_status === QuestionnaireStatusEnum.NoAnswers ||
            ((this.questionnaire.cifc_status === QuestionnaireStatusEnum.InProgress ||
                this.questionnaire.cifc_status === QuestionnaireStatusEnum.Updated) &&
                !this.questionnaire.has_answered_required) ||
            this.questionnaire.required_questions_no === 0
        );
    }

    public getSections(): Section[] {
        return this.sections;
    }

    public getCurrentSectionTitle() {
        const getSection = this.sections.find((x) => x.id === this.question?.section_id);
        return 'SECTION ' + getSection?.number + '. ' + getSection?.section;
    }

    public setQuestion(question: Question) {
        this.questionSubject.next(question);
        this.question = _.cloneDeep(question);
    }

    public getQuestionObs(): Observable<Question | null> {
        return this.questionSubject.asObservable();
    }

    public isQuestionnaireLoaded(): Observable<boolean> {
        return this.questionnaireLoadedSubject.asObservable();
    }

    public getQuestion(): Question {
        return this.question;
    }

    public setDisplayVersion(version: string) {
        this.displayVersion = version;
        this.flattenSections();
        this.questionnaireResumed = true;
        this.displayVersionSubject.next(version);
    }

    public getDisplayVersionObs(): Observable<string> {
        return this.displayVersionSubject.asObservable();
    }

    public markQuestionAsComplete(question: Question) {
        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            this.question = this.commonQuestionnaireService.currentEntity.entity as Question;
            this.question.answerChanged = question.answerChanged;
            if (this.question.body.answered && (this.question as unknown as QuestionResponse).body.deleted) {
                (this.question as unknown as QuestionResponse).body.deleted = false;
            }
        } else {
            this.question = question;
        }

        (this.question as unknown as QuestionResponse).body.answered = true;
        /** mark question as accepted and review as solved if the question already has a registered review (amend/modify) */
        if (
            this.commonQuestionnaireService.reviewMode &&
            this.question.cifc_review &&
            !this.question.cifc_review.solved &&
            !(this.question as unknown as QuestionResponse).body.accepted &&
            !this.question.cifc_review.recent
        ) {
            (this.question as unknown as QuestionResponse).body.accepted = true;
            this.question.cifc_review.solved = true;
        }

        /** mark question as accepted if the question it's clean, and it's not already accepted */
        if (
            this.commonQuestionnaireService.reviewMode &&
            !this.question.cifc_review &&
            !(this.question as unknown as QuestionResponse).body.accepted
        ) {
            (this.question as unknown as QuestionResponse).body.accepted = true;
        }

        if (
            !this.commonQuestionnaireService.reviewMode &&
            !this.commonQuestionnaireService.createMode &&
            this.question.cifc_review &&
            !this.question.cifc_review.solved
        ) {
            this.question.cifc_review.answered = true;
            const originalQuestion = this.getInitialQuestionById(this.question.id || '');

            /** mark question as accepted and solved if notification type is modify. We don't need another accept from CIFC */
            if (
                this.question.cifc_review.notification_type === 'modify' &&
                originalQuestion &&
                !this.commonQuestionnaireService.isQuestionBodyModified(this.question, originalQuestion)
            ) {
                (this.question as unknown as QuestionResponse).body.accepted = true;
                this.question.cifc_review.solved = true;
            }
        }

        if (this.commonQuestionnaireService.reviewMode && !this.question.cifc_review?.reason) {
            /** write a default reason if it's not specified */
            this.question.cifc_review.reason = 'No reason specified';
        }

        const questionAlreadyInArrayIndex = this.modifiedQuestions.findIndex((x) => x.id === this.question.id);
        if (questionAlreadyInArrayIndex < 0) {
            this.modifiedQuestions.push(_.cloneDeep(this.question as unknown as QuestionResponse));
        } else {
            this.modifiedQuestions[questionAlreadyInArrayIndex] = this.question as unknown as QuestionResponse;
        }

        this.markQuestionAsCompleteSubject.next(this.question);
        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            this.saveQuestionForAutoSubmit();
        }
        this.nextQuestion();
    }

    public removeQuestionFromReview(questionId: string) {
        const index = this.modifiedQuestions.findIndex((x) => x.id === questionId);

        if (index > -1) {
            this.modifiedQuestions.splice(index, 1);
        }
    }

    public onQuestionMarked(): Observable<Question> {
        return this.markQuestionAsCompleteSubject.asObservable();
    }

    public areAllMandatoryQuestionsAnswered(): boolean {
        let totalQuestions = 0;
        let totalAnswers = 0;

        this.sections.forEach((section: Section) => {
            totalAnswers += section.questions.filter(
                (x) =>
                    (x as unknown as QuestionResponse).body.answered &&
                    !(x as unknown as QuestionResponse).body.deleted &&
                    !x.hidden &&
                    x.required,
            ).length;
            totalQuestions += section.questions.filter((x) => (x as unknown as QuestionResponse).required && !x.hidden).length;
        });
        return totalAnswers > 0 && totalQuestions === totalAnswers;
    }

    public areAllQuestionsAnswered(): boolean {
        let totalQuestions = 0;
        let totalAnswers = 0;

        this.sections.forEach((section: Section) => {
            totalAnswers += section.questions.filter(
                (x) =>
                    (x as unknown as QuestionResponse).body.answered &&
                    !(x as unknown as QuestionResponse).body.deleted &&
                    !x.hidden &&
                    (x.cifc_review ? x.cifc_review?.solved || x.cifc_review.answered : true),
            ).length;

            totalQuestions += section.questions.filter((x) => !x.hidden).length;
        });

        return totalAnswers > 0 && totalQuestions === totalAnswers;
    }

    public answeredAtLeastOneNonRequiredQuestion(): boolean {
        let totalAnswers = 0;

        this.sections.forEach((section: Section) => {
            totalAnswers += section.questions.filter(
                (x) =>
                    (x as unknown as QuestionResponse).body.answered &&
                    !(x as unknown as QuestionResponse).body.deleted &&
                    !x.hidden &&
                    !x.required &&
                    (x.cifc_review ? x.cifc_review?.solved || x.cifc_review.answered : true),
            ).length;
        });

        return totalAnswers > 0;
    }

    public calculateTotalNumberOfQuestions(): number {
        let totalFull = 0;
        let totalCondensed = 0;

        this.sections.forEach((section: Section) => {
            if (this.displayVersion === 'full') {
                totalFull += section.questions.filter((question: Question) => !question.hidden).length;
            } else {
                section.questions.forEach((question: Question) => {
                    if (
                        (!question.hidden && this.displayVersion === 'full' && !question.required) ||
                        (this.displayVersion === 'condensed' && question.required && !question.hidden)
                    ) {
                        totalCondensed += 1;
                    }
                });
            }
        });

        return this.displayVersion === 'full' ? totalFull : totalCondensed;
    }

    public calculateAnsweredQuestions(): number {
        let totalFull = 0;
        let totalCondensed = 0;

        this.sections.forEach((section: Section) => {
            totalFull += section.questions.filter(
                (x) =>
                    (x as unknown as QuestionResponse).body.answered &&
                    !(x as unknown as QuestionResponse).body.deleted &&
                    (x.cifc_review ? x.cifc_review.solved || x.cifc_review.answered : true) &&
                    !x.hidden,
            ).length;
            totalCondensed += section.questions.filter(
                (x) =>
                    x.required &&
                    (x as unknown as QuestionResponse).body.answered &&
                    !(x as unknown as QuestionResponse).body.deleted &&
                    (x.cifc_review ? x.cifc_review.solved || x.cifc_review.answered : true) &&
                    !x.hidden,
            ).length;
        });

        return this.displayVersion === 'full' ? totalFull : totalCondensed;
    }

    public buildQuestionnaire(questionnaire: Questionnaire) {
        this.questionnaire.questionnaire = this.sections;
        this.questionnaire.has_answered_required = questionnaire.has_answered_required;
        this.questionnaire.cifc_status = questionnaire.cifc_status;
        this.questionnaire.required_questions_no = questionnaire.required_questions_no;
    }

    public getAnsweredQuestionsForReview(): Question[] {
        let answeredQuestions: Question[] = [];

        for (let section of this.sections) {
            for (let question of section.questions) {
                let answeredQuestion = question as unknown as QuestionResponse;
                if (answeredQuestion.body.answered && !answeredQuestion.body.deleted) {
                    answeredQuestions.push(answeredQuestion as unknown as Question);
                }
            }
        }

        return answeredQuestions;
    }

    public getMandatoryQuestionsForReview(): Question[] {
        let questions: Question[] = [];

        for (let question of this.getAnsweredQuestionsForReview()) {
            if (question.required && !question.hidden) {
                questions.push(question);
            }
        }

        return questions;
    }

    public flattenSections() {
        let index = 0;

        this.processedFullSections = [];
        this.processedCondensedSections = [];

        for (let section of this.sections) {
            for (let question of section.questions) {
                question.number = index;

                if (!question.hidden) {
                    this.processedFullSections.push(question);
                }
                if (question.required && !question.hidden) {
                    this.processedCondensedSections.push(question);
                }

                index++;
            }
        }

        if (this.processedFullSections.length) {
            this.commonQuestionnaireService.questionsFilteredSubject.next(true);
        }
    }

    public currentQuestionIndex(specificVersion?: 'full' | 'condensed'): number {
        let version = specificVersion ?? this.displayVersion;

        return version === 'full'
            ? this.processedFullSections.findIndex((question: Question) => question.id === this.question?.id)
            : this.processedCondensedSections.findIndex((question: Question) => question.id === this.question?.id);
    }

    public checkNextAvailability() {
        return this.currentQuestionIndex() === this.calculateTotalNumberOfQuestions() - 1;
    }

    public checkPreviousAvailability() {
        return this.currentQuestionIndex() === 0;
    }

    public nextQuestion() {
        let currIndex = this.currentQuestionIndex();
        if (this.displayVersion === 'condensed' && !this.question.required) {
            let closestQuestion: Question;

            /** find index in original array */
            currIndex = this.currentQuestionIndex('full');

            /** search in the right part of the question if there are any condensed questions left; return the first one */
            let condensedQuestionsRight = this.processedFullSections
                .slice(currIndex)
                .filter((question: Question) => question.required && !question.hidden);
            closestQuestion = condensedQuestionsRight[0];

            /** if no questions are available in the right, search in the left part; return the first one after reversing the resulted array */
            if (!condensedQuestionsRight.length) {
                let condensedQuestionsLeft = this.processedFullSections
                    .slice(0, currIndex)
                    .reverse()
                    .filter((question: Question) => question.required && !question.hidden);

                closestQuestion = condensedQuestionsLeft[0];
            }

            this.setQuestion(closestQuestion);
            this.commonQuestionnaireService.onQuestionSelected(closestQuestion as unknown as QuestionResponse);

            return;
        }

        const questionToDisplay =
            this.displayVersion === 'full'
                ? this.processedFullSections[currIndex + 1 !== this.processedFullSections.length ? currIndex + 1 : currIndex]
                : this.processedCondensedSections[currIndex + 1 !== this.processedCondensedSections.length ? currIndex + 1 : currIndex];

        this.setQuestion(questionToDisplay);
        this.commonQuestionnaireService.onQuestionSelected(questionToDisplay as unknown as QuestionResponse);
    }

    public atLeastOneCondensedQuestion(): boolean {
        return this.processedCondensedSections.length > 0;
    }

    public previousQuestion() {
        let currIndex = this.currentQuestionIndex();
        const questionToDisplay =
            this.displayVersion === 'full' ? this.processedFullSections[currIndex - 1] : this.processedCondensedSections[currIndex - 1];

        this.setQuestion(questionToDisplay);
        this.commonQuestionnaireService.onQuestionSelected(questionToDisplay as unknown as QuestionResponse);
    }

    public onAnotherOptionChange(question: Question, option: any) {
        question.answerChanged = !!option;
    }

    public async submitQuestionsForReview() {
        this.isLoadingSubmit = true;

        await this.cifcService
            .submitReview(this.modifiedQuestions)
            .then((result) => {
                this.isLoadingSubmit = false;
                this.toastService.showSuccess('Review added.', { duration: 2000 });

                for (const question of this.modifiedQuestions) {
                    const draftAnswer = {
                        id: question.body?.id || uuid.v4(),
                        question_id: question.body.question_id,
                        company_id: question.body.company_id ?? '',
                        type: question.body.type,
                        options: question.body.options,
                        categories: question.type_id === QuestionTypesEnum.MATRIX ? question.body.categories : [],
                        accepted: question.body.accepted,
                        solved: question.cifc_review?.solved,
                    };

                    let deleteAnswers = this.setAnswersForDelete(draftAnswer);

                    if (deleteAnswers.length) {
                        this.isLoadingSubmit = true;
                        deleteAnswers.forEach((answerId: string) => {
                            this.deleteMultiple(`answers/${this.companyId || question.body.company_id}/`, {
                                body: deleteAnswers,
                            }).subscribe(
                                (result) => {
                                    this.isLoadingSubmit = false;
                                },
                                (err) => {
                                    this.isLoadingSubmit = false;
                                    this.toastService.showError('Could not delete answer', { duration: 2000 });
                                },
                            );
                        });
                        if (deleteAnswers.length) {
                            this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, this.identityService.company?.id);
                        }
                    }
                }
            })
            .catch((err) => {
                this.isLoadingSubmit = false;
                this.toastService.showError('Review could not be added.', { duration: 2000 });
            });

        this.modifiedQuestions = [];
    }

    public buildQuestionsArrayForSubmitting() {
        let answers: any[] = [];
        for (const question of this.modifiedQuestions) {
            answers.push({
                id: question.body?.id || null,
                question_id: question.body.question_id,
                company_id: question.body.company_id ?? '',
                type: question.body.type,
                options: _.cloneDeep(question.body.options),
                categories: question.type_id === QuestionTypesEnum.MATRIX ? question.body.categories : [],
            });
        }
        return answers;
    }

    public checkRequiredQuestionsAnswered() {
        let answered = true;

        for (const question of this.processedCondensedSections) {
            if (!question.body.answered) {
                answered = false;
                break;
            }
        }

        return answered;
    }

    public async submitQuestions() {
        let resourceId = '/v2/customer-questionnaire/questionnaire/answers/' + this.companyId;

        const answers = this.buildQuestionsArrayForSubmitting();
        this.isLoading = true;
        if (answers.length) {
            this.http.post(resourceId, answers).subscribe((result) => {
                this.isLoading = false;
                this.hasSubmittedQuestionnaire = true;
                this.showCheckSubmit = false;
                this.modifiedQuestions = [];
                localStorage.setItem('hasSubmittedQuestionnaire', 'true');
                this.toastService.showSuccess('Your answers have been successfully submitted.', { duration: 5000 });
                this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, this.companyId, true);
                this.questionnaireResumed = false;
            });
        }
    }

    public saveQuestionForAutoSubmit() {
        this.isLoadingSubmit = true;
        const questionFormated = this.question as unknown as QuestionResponse;
        const draftAnswer = {
            id: questionFormated.body?.id || uuid.v4(),
            question_id: questionFormated.body.question_id,
            company_id: questionFormated.body.company_id ?? '',
            type: questionFormated.body.type,
            options: questionFormated.body.options,
            categories: questionFormated.type_id === QuestionTypesEnum.MATRIX ? questionFormated.body.categories : [],
            accepted: questionFormated.body.accepted,
            solved: questionFormated.cifc_review?.solved,
            deleted: questionFormated.body.deleted,
            updated: questionFormated.body?.id ? !questionFormated.cifc_review || this.question['answerChanged'] : false,
            answerChanged: this.question.answerChanged,
            hasModifyReview: questionFormated.cifc_review && questionFormated.cifc_review.notification_type === 'modify',
        };

        if (this.question.body) {
            (this.question.body as unknown as QuestionResponse).id = draftAnswer.id;
        }

        this.http.post(`${this.versionPrefix}/${this.resourceName}/answers/` + this.companyId + '/draft', draftAnswer).subscribe(
            (result) => {
                this.isLoadingSubmit = false;
                this.answerSavedSubject.next(true);
            },
            (err) => {
                this.isLoadingSubmit = false;
                this.toastService.showError('Could not save answer.', {
                    duration: 2000,
                });
            },
            () => {
                this.question.answerChanged = false;
                this.isLoadingSubmit = false;

                let deleteAnswers = this.setAnswersForDelete(draftAnswer);

                if (deleteAnswers.length) {
                    this.isLoadingSubmit = true;
                    this.deleteMultiple(`answers/${this.companyId}/`, { body: deleteAnswers }).subscribe(
                        (result) => {
                            this.isLoadingSubmit = false;
                        },
                        (err) => {
                            this.isLoadingSubmit = false;
                            this.toastService.showError('Could not delete answer', { duration: 2000 });
                        },
                    );
                    if (deleteAnswers.length) {
                        this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, this.identityService.company?.id);
                    }
                }
            },
        );
    }

    public getModifiedQuestionsLength(): number {
        return this.modifiedQuestions.length;
    }

    public getInitialQuestionById(id: string): Question | null {
        for (const section of this.initialSections) {
            const question = section.questions.find((q: Question) => q.id === id);

            if (question) {
                return question;
            }
        }

        return null;
    }

    public setAnswersForDelete(answer: any) {
        let deleteAnswers: string[] = [];

        answer.options?.forEach((op: any) => {
            if (!op.checked && op.target_question_list && op.target_question_list.length) {
                op.target_question_list.forEach((q: any) => {
                    this.sections.forEach((section: Section) => {
                        const findElement = (section.questions as unknown as QuestionResponse[]).find(
                            (x: QuestionResponse) => x.id === q.question_id,
                        );

                        if (findElement && findElement.body?.answered && !findElement.body?.deleted) {
                            deleteAnswers.push(findElement?.body.id || '');
                        }
                    });
                });
            }
        });

        deleteAnswers = deleteAnswers.filter((n) => n);
        return deleteAnswers;
    }
}
